import React, { useState, useContext, useEffect } from 'react';
import { callApi } from '../callApi';
import { AppContext } from '../components/AppContext';
import {
  Box,
  Grid,
  GridItem,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Select,
  useToast,
  Divider,
  Alert,
  AlertIcon,
  Flex,
  Spinner,
  Badge,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure
} from "@chakra-ui/react";
import { RepeatIcon, EditIcon } from '@chakra-ui/icons';
import HeaderMenus from './HeaderMenus';

function ClientManagement() {
  // State for data
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [clients, setClients] = useState([]);
  const [clientTypes, setClientTypes] = useState([]);
  
  // Loading states
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isCreatingClient, setIsCreatingClient] = useState(false);
  const [isUpdatingClient, setIsUpdatingClient] = useState(false);
  
  // Form states for new client
  const [newClientName, setNewClientName] = useState('');
  const [newClientType, setNewClientType] = useState('');
  const [newClientDescription, setNewClientDescription] = useState('');
  const [newClientCaseTypeHint, setNewClientCaseTypeHint] = useState('');
  const [newClientEmRiskHint, setNewClientEmRiskHint] = useState('');
  const [newClientEmProblemHint, setNewClientEmProblemHint] = useState('');
  const [newClientEmAnalysisHint, setNewClientEmAnalysisHint] = useState('');
  
  // Edit modal states
  const [editingClient, setEditingClient] = useState(null);
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCaseTypeHint, setEditCaseTypeHint] = useState('');
  const [editEmRiskHint, setEditEmRiskHint] = useState('');
  const [editEmProblemHint, setEditEmProblemHint] = useState('');
  const [editEmAnalysisHint, setEditEmAnalysisHint] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const { accessToken } = useContext(AppContext);
  const toast = useToast();
  
  // Load organizations
  const loadOrgs = async () => {
    setIsLoadingOrgs(true);
    try {
      const response = await callApi('/orgs', accessToken, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch organizations');
      }
      
      const data = await response.json();
      if (data && data.orgs && Array.isArray(data.orgs)) {
        setOrgs(data.orgs);
      } else {
        setOrgs([]);
        console.warn('Received invalid orgs data:', data);
      }
    } catch (err) {
      console.error('Error loading organizations:', err);
      toast({
        title: 'Error loading organizations',
        description: 'Could not retrieve organization list.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setOrgs([]);
    } finally {
      setIsLoadingOrgs(false);
    }
  };
  
  // Load client types
  const loadClientTypes = async () => {
    try {
      const response = await callApi('/client-types', accessToken, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch client types');
      }
      
      const data = await response.json();
      if (data && data.client_types && Array.isArray(data.client_types)) {
        setClientTypes(data.client_types);
      } else {
        setClientTypes([]);
        console.warn('Received invalid client types data:', data);
      }
    } catch (err) {
      console.error('Error loading client types:', err);
      toast({
        title: 'Error loading client types',
        description: 'Could not retrieve client type list.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setClientTypes([]);
    }
  };
  
  // Load clients for selected org
  const loadClients = async (orgId) => {
    if (!orgId) {
      setClients([]);
      return;
    }
    
    setIsLoadingClients(true);
    try {
      const response = await callApi(`/clients?org_id=${orgId}`, accessToken, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch clients');
      }
      
      const data = await response.json();
      if (data && data.clients && Array.isArray(data.clients)) {
        setClients(data.clients);
      } else {
        setClients([]);
        console.warn('Received invalid clients data:', data);
      }
    } catch (err) {
      console.error('Error loading clients:', err);
      toast({
        title: 'Error loading clients',
        description: 'Could not retrieve client list.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setClients([]);
    } finally {
      setIsLoadingClients(false);
    }
  };
  
  // Handle org selection change
  const handleOrgChange = (event) => {
    const orgId = event.target.value;
    setSelectedOrg(orgId);
    loadClients(orgId);
  };
  
  // Create new client
  const handleCreateClient = async (event) => {
    event.preventDefault();
    
    // Debug logging
    console.log('Creating client with:', {
      selectedOrg,
      selectedOrgParsed: parseInt(selectedOrg),
      newClientName,
      newClientType,
      newClientDescription
    });
    
    // Basic validation
    if (!selectedOrg || !newClientName || !newClientType) {
      toast({
        title: 'Missing information',
        description: 'Please select an organization and provide client name and type.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    const orgId = parseInt(selectedOrg, 10);
    if (isNaN(orgId) || orgId < 0) {
      toast({
        title: 'Invalid organization',
        description: 'Please select a valid organization.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsCreatingClient(true);
    try {
      const requestBody = {
        org_id: orgId,
        name: newClientName,
        client_type: newClientType,
        description: newClientDescription,
        configuration: {
          case_type_hint: newClientCaseTypeHint || null,
          em_risk_hint: newClientEmRiskHint || null,
          em_problem_hint: newClientEmProblemHint || null,
          em_analysis_hint: newClientEmAnalysisHint || null
        },
        active: true
      };
      
      console.log('Request body:', requestBody);
      
      const response = await callApi('/clients', accessToken, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      
      const data = await response.json();
      
      console.log('Response status:', response.status);
      console.log('Response data:', data);
      
      if (response.ok) {
        toast({
          title: 'Client created',
          description: `${newClientName} has been added successfully.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        // Clear form
        setNewClientName('');
        setNewClientType('');
        setNewClientDescription('');
        setNewClientCaseTypeHint('');
        setNewClientEmRiskHint('');
        setNewClientEmProblemHint('');
        setNewClientEmAnalysisHint('');
        
        // Refresh client list
        loadClients(selectedOrg);
      } else {
        console.error('Client creation failed:', data);
        toast({
          title: 'Creation failed',
          description: data.error || 'Failed to create client.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error creating client:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred while creating the client.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsCreatingClient(false);
    }
  };
  
  // Open edit modal
  const handleEditClient = (client) => {
    setEditingClient(client);
    setEditName(client.name);
    setEditType(client.client_type);
    setEditDescription(client.description || '');
    setEditCaseTypeHint((client.configuration && client.configuration.case_type_hint) || '');
    setEditEmRiskHint((client.configuration && client.configuration.em_risk_hint) || '');
    setEditEmProblemHint((client.configuration && client.configuration.em_problem_hint) || '');
    setEditEmAnalysisHint((client.configuration && client.configuration.em_analysis_hint) || '');
    onOpen();
  };
  
  // Update client
  const handleUpdateClient = async () => {
    if (!editName || !editType) {
      toast({
        title: 'Missing information',
        description: 'Please provide client name and type.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsUpdatingClient(true);
    try {
      const response = await callApi(`/clients/${editingClient.id}`, accessToken, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: editName,
          client_type: editType,
          description: editDescription,
          configuration: {
            case_type_hint: editCaseTypeHint || null,
            em_risk_hint: editEmRiskHint || null,
            em_problem_hint: editEmProblemHint || null,
            em_analysis_hint: editEmAnalysisHint || null
          },
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: 'Client updated',
          description: `${editName} has been updated successfully.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        onClose();
        loadClients(selectedOrg);
      } else {
        toast({
          title: 'Update failed',
          description: data.error || 'Failed to update client.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error updating client:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred while updating the client.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsUpdatingClient(false);
    }
  };
  
  // Toggle client active status
  const handleToggleClientStatus = async (client) => {
    try {
      const response = await callApi(`/clients/${client.id}`, accessToken, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          active: !client.active,
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: 'Client status updated',
          description: `${client.name} has been ${!client.active ? 'activated' : 'deactivated'}.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        loadClients(selectedOrg);
      } else {
        toast({
          title: 'Update failed',
          description: data.error || 'Failed to update client status.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error updating client status:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred while updating the client status.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };
  
  // Load data on component mount
  useEffect(() => {
    loadOrgs();
    loadClientTypes();
    
    // Debug: Check user context
    console.log('User context:', { accessToken: accessToken ? 'present' : 'missing' });
  }, []);
  
  // Get selected org name for display
  const selectedOrgName = orgs.find(org => org.id.toString() === selectedOrg)?.name || '';
  
  return (
    <Box textAlign="center" fontSize="xl">
      <Grid
        templateAreas={`"header header"    
                        "main main"
                        "footer footer"`}
        gridTemplateRows={'60px auto 1fr 60px'}
        gridTemplateColumns={'1fr 1fr'}
        h='100%'
        gap='2'
        color='blackAlpha.800'
        fontSize='12px'
      >
        <HeaderMenus />

        <GridItem bg='gray.50' padding={6} area={'main'}>
          <Box maxWidth="1200px" margin="0 auto">
            <Heading as="h1" size="lg" mb={6} textAlign="left">
              Client Management
            </Heading>
            
            {/* Organization Selection */}
            <Box bg="white" p={5} borderRadius="md" boxShadow="sm" mb={6}>
              <Flex justify="space-between" align="center" mb={4}>
                <Heading as="h2" size="md" textAlign="left">
                  Select Organization
                </Heading>
                <Button 
                  leftIcon={<RepeatIcon />}
                  onClick={loadOrgs}
                  isLoading={isLoadingOrgs}
                  size="sm"
                >
                  Refresh
                </Button>
              </Flex>
              
              <FormControl>
                <Select
                  placeholder="Choose an organization..."
                  value={selectedOrg}
                  onChange={handleOrgChange}
                  size="lg"
                >
                  {orgs.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            {/* Add New Client Form */}
            {selectedOrg && (
              <Box bg="white" p={5} borderRadius="md" boxShadow="sm" mb={6}>
                <Heading as="h2" size="md" mb={4} textAlign="left">
                  Add New Client to {selectedOrgName}
                </Heading>
                
                {/* Debug info */}
                <Text fontSize="xs" color="gray.500" mb={2}>
                  Debug: Selected Org ID = {selectedOrg}, Org Name = {selectedOrgName}
                </Text>
                
                <form onSubmit={handleCreateClient}>
                  <Flex gap={4} flexWrap="wrap" mb={4}>
                    <FormControl isRequired flex="1" minW="200px">
                      <FormLabel>Client Name</FormLabel>
                      <Input
                        type="text"
                        value={newClientName}
                        onChange={(e) => setNewClientName(e.target.value)}
                        placeholder="e.g., Nursing Facility Visits"
                      />
                    </FormControl>
                    
                    <FormControl isRequired flex="1" minW="200px">
                      <FormLabel>Client Type</FormLabel>
                      <Select
                        value={newClientType}
                        onChange={(e) => setNewClientType(e.target.value)}
                        placeholder="Select client type..."
                      >
                        {clientTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </Flex>
                  
                  <FormControl mb={4}>
                    <FormLabel>Description (Optional)</FormLabel>
                    <Input
                      type="text"
                      value={newClientDescription}
                      onChange={(e) => setNewClientDescription(e.target.value)}
                      placeholder="Brief description of this client's needs or context"
                    />
                  </FormControl>

                  <FormControl mb={4}>
                    <FormLabel>Case Type Hint (Optional)</FormLabel>
                    <Input
                      type="text"
                      value={newClientCaseTypeHint}
                      onChange={(e) => setNewClientCaseTypeHint(e.target.value)}
                      placeholder="e.g., This client primarily handles nursing facility visits with complex wound care"
                    />
                  </FormControl>

                  <FormControl mb={4}>
                    <FormLabel>E/M Risk Assessment Hint (Optional)</FormLabel>
                    <Input
                      type="text"
                      value={newClientEmRiskHint}
                      onChange={(e) => setNewClientEmRiskHint(e.target.value)}
                      placeholder="e.g., Be conservative when assessing risk factors. Require clear evidence for moderate/high risk."
                    />
                  </FormControl>

                  <FormControl mb={4}>
                    <FormLabel>E/M Problem Categorization Hint (Optional)</FormLabel>
                    <Input
                      type="text"
                      value={newClientEmProblemHint}
                      onChange={(e) => setNewClientEmProblemHint(e.target.value)}
                      placeholder="e.g., Apply conservative problem categorization. Choose lower complexity when uncertain."
                    />
                  </FormControl>

                  <FormControl mb={4}>
                    <FormLabel>E/M Data Analysis Hint (Optional)</FormLabel>
                    <Input
                      type="text"
                      value={newClientEmAnalysisHint}
                      onChange={(e) => setNewClientEmAnalysisHint(e.target.value)}
                      placeholder="e.g., Use strict criteria for data analysis categories. Only count clear examples."
                    />
                  </FormControl>

                  <Button
                    colorScheme="blue"
                    type="submit"
                    isLoading={isCreatingClient}
                  >
                    Add Client
                  </Button>
                </form>
              </Box>
            )}
            
            {/* Client List */}
            {selectedOrg && (
              <>
                <Flex justify="space-between" align="center" mb={4}>
                  <Heading as="h2" size="md" textAlign="left">
                    Clients for {selectedOrgName}
                  </Heading>
                  <Button 
                    leftIcon={<RepeatIcon />}
                    onClick={() => loadClients(selectedOrg)}
                    isLoading={isLoadingClients}
                    size="sm"
                  >
                    Refresh
                  </Button>
                </Flex>
                
                {isLoadingClients ? (
                  <Flex justify="center" my={8}>
                    <Spinner size="xl" />
                  </Flex>
                ) : clients.length > 0 ? (
                  <Box overflowX="auto" bg="white" borderRadius="md" boxShadow="sm">
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Name</Th>
                          <Th>Type</Th>
                          <Th>Description</Th>
                          <Th>Status</Th>
                          <Th>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {clients.map((client) => (
                          <Tr key={client.id}>
                            <Td fontWeight="medium">{client.name}</Td>
                            <Td>
                              {clientTypes.find(type => type.value === client.client_type)?.label || client.client_type}
                            </Td>
                            <Td>{client.description || 'N/A'}</Td>
                            <Td>
                              <Badge 
                                colorScheme={client.active ? 'green' : 'gray'}
                                cursor="pointer"
                                onClick={() => handleToggleClientStatus(client)}
                              >
                                {client.active ? 'Active' : 'Inactive'}
                              </Badge>
                            </Td>
                            <Td>
                              <IconButton
                                aria-label="Edit client"
                                icon={<EditIcon />}
                                size="sm"
                                onClick={() => handleEditClient(client)}
                              />
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                ) : (
                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <Text>No clients found for this organization. Add a client to get started.</Text>
                  </Alert>
                )}
              </>
            )}
            
            {!selectedOrg && (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text>Please select an organization to view and manage its clients.</Text>
              </Alert>
            )}
          </Box>
        </GridItem>
      </Grid>
      
      {/* Edit Client Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Client</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired mb={4}>
              <FormLabel>Client Name</FormLabel>
              <Input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Client name"
              />
            </FormControl>
            
            <FormControl isRequired mb={4}>
              <FormLabel>Client Type</FormLabel>
              <Select
                value={editType}
                onChange={(e) => setEditType(e.target.value)}
                placeholder="Select client type..."
              >
                {clientTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            
            <FormControl mb={4}>
              <FormLabel>Description</FormLabel>
              <Input
                type="text"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Brief description"
              />
            </FormControl>
            
            <FormControl mb={4}>
              <FormLabel>Case Type Hint</FormLabel>
              <Input
                type="text"
                value={editCaseTypeHint}
                onChange={(e) => setEditCaseTypeHint(e.target.value)}
                placeholder="e.g., This client primarily handles nursing facility visits"
              />
            </FormControl>
            
            <FormControl mb={4}>
              <FormLabel>E/M Risk Assessment Hint</FormLabel>
              <Input
                type="text"
                value={editEmRiskHint}
                onChange={(e) => setEditEmRiskHint(e.target.value)}
                placeholder="e.g., Be conservative when assessing risk factors"
              />
            </FormControl>
            
            <FormControl mb={4}>
              <FormLabel>E/M Problem Categorization Hint</FormLabel>
              <Input
                type="text"
                value={editEmProblemHint}
                onChange={(e) => setEditEmProblemHint(e.target.value)}
                placeholder="e.g., Apply conservative problem categorization"
              />
            </FormControl>
            
            <FormControl>
              <FormLabel>E/M Data Analysis Hint</FormLabel>
              <Input
                type="text"
                value={editEmAnalysisHint}
                onChange={(e) => setEditEmAnalysisHint(e.target.value)}
                placeholder="e.g., Use strict criteria for data analysis"
              />
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleUpdateClient}
              isLoading={isUpdatingClient}
            >
              Update Client
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default ClientManagement;